import { Component, Inject, OnInit, Optional } from '@angular/core';
import { ValueSetterParams } from '@ag-grid-community/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { RememberService } from '@app/core/services/remember.service';
import { StateService } from '@app/core/services/state.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { EquipmentItem, EquipmentService } from '@app/services/equipment.service';
import { FactoryItem, FactoryService } from '@app/services/factory.service';
import { LogisticService, UnitItem } from '@app/services/logistic.service';
import { OperationService } from '@app/services/operation.service';
import { WorkEdit, WorkLinkType, WorkOperationItem, WorkSaveRecord, WorkService, WorkSuboperationRow } from '@app/services/work.service';
import { WorkGroupItem, WorkGroupService } from '@app/services/work-group.service';

@Component({
    selector: 'tt-work',
    templateUrl: './work.component.html',
    styleUrls: ['./work.component.css'],
    standalone: false
})
export class WorkComponent implements OnInit {
    public page?: WorkEdit;

    public grid?: GridOptions<WorkSuboperationRow>;

    public selectListFactories: FactoryItem[] = [];

    public selectListOperations: WorkOperationItem[] = [];

    public selectListWorkGroups: WorkGroupItem[] = [];

    public selectListEquipments: EquipmentItem[] = [];

    public selectListUnits: UnitItem[] = [];

    public selectListLinkTypes: WorkLinkType[] = [];

    public saving = false;

    public deleting = false;

    constructor(
        private work: WorkService,
        private factory: FactoryService,
        private workGroups: WorkGroupService,
        private equipment: EquipmentService,
        private logistic: LogisticService,
        private operation: OperationService,
        private remember: RememberService,
        private state: StateService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public get isExistingWork(): boolean { return Number(this.page?.work_keyno ?? 0) > 0; }

    public async onFactoryChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.factory_id) return;

        this.page.factory_id = value;

        if (!value) return;

        void this.remember.remember('w_work', JSON.stringify(value));
        await Promise.all([this.loadOperations(), this.loadWorkGroups(), this.loadEquipments()]);
    }

    public async onOperationChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.operation_id) return;

        this.page.operation_id = value;

        if (!value) return;

        await this.retrieveOperation();
        await this.modifyOperation();
        await Promise.all([this.loadWorkGroups(), this.loadEquipments(), this.loadGrid()]);
    }

    public async onWorkGroupChanged(value: string): Promise<void> {
        if (!this.page || value === this.page.workgrp_keyno) return;

        this.page.workgrp_keyno = value;

        if (value) await this.loadEquipments();
    }
    
    public async saveChanges() {
	    if (!this.page) return;

        this.saving = true;
	
	    try {
		    const rows = this.grid?.gridfunc?.getAllRows() ?? [];

            this.page.records = rows.map((row): WorkSaveRecord => ({
                suboperation_keyno: row.suboperation_keyno,
                quantity: row.quantity,
                quantity_in_work_units: row.quantity_in_work_units,
                time_adjust_factor: row.time_adjust_factor,
                adjusted_quantity_in_work_units: row.adjusted_quantity_in_work_units,
            }));

            const response = await this.work.saveObj(this.page);

            if (response?.errorcode !== '0') {
			    this.modal.show({ 
				    type: 'warning', 
				    title: 'error', 
				    translateTitle: true, 
				    message: response.errormessage 
			    });
		    } else {
			    this.navigator?.back();
		    }
	    } finally {
		    this.saving = false;
	    }
    }
	
    public async deleteValues() {
	    if (!!this.page) {
			this.deleting = true;

		    try {
			    const deleteConfirmed = await this.modal.openConfirmDialog({
				    title: 'confirm_title',
				    translateMessage: true,
				    message: 'confirm_message',
				    type: 'danger'
			    });
			
			    if (deleteConfirmed) {
				    const response = await this.work.deleteItem({ work_keyno: this.page.work_keyno });
				
				    if (response.errorcode !== '0') {
					    this.modal.show({
						    type: 'warning',
						    title: 'error',
						    message: response.errormessage
					    });
				    } else {
					    this.navigator?.back();
				    }
			    }
		    } finally {
			    this.deleting = false;
		    }
	    }
    }
	
    private async initialize(): Promise<void> {
        const params = this.state.getStateParams();

        this.page = await this.work.getEdit({
            prod_id: params['prod_id'] ?? '',
            work_keyno: params['work_keyno'] ?? ''
        });

        const [linkTypes, units] = await Promise.all([
            this.work.listLinkTypes(),
            this.logistic.getUnitList({ onlytimeunits: '1', isactive: '1' })
        ]);

        this.selectListLinkTypes = linkTypes;
        this.selectListUnits = units;

        if (!this.page) return;

        if (this.page.businessco_no) this.selectListFactories = await this.factory.getFactories({ businessco_no: this.page.businessco_no });

        await this.loadGrid();

        if (this.page.work_keyno === '0') {
            const rememberedFactory = await this.remember.getLastStatus('w_work', true) as unknown as string | null;

            if (rememberedFactory) this.page.factory_id = rememberedFactory;
        }

        await Promise.all([this.loadOperations(), this.loadWorkGroups(), this.loadEquipments()]);
    }

    private async loadOperations(): Promise<void> {
        if (!this.page?.factory_id) return;

        this.selectListOperations = await this.work.listOperations({
            direct: '1',
            factory_id: this.page.factory_id
        });
    }

    private async loadWorkGroups(): Promise<void> {
        if (!this.page?.factory_id || !this.page.operation_id) return;

        this.selectListWorkGroups = await this.workGroups.getWorkGroups({
            workgrp_keyno: this.page.workgrp_keyno,
            operation_id: this.page.operation_id,
            factory_id: this.page.factory_id
        });
    }

    private async loadEquipments(): Promise<void> {
        if (!this.page?.factory_id || !this.page.operation_id) return;

        this.selectListEquipments = await this.equipment.getEquipments({
            workgrp_keyno: this.page.workgrp_keyno,
            operation_id: this.page.operation_id,
            factory_id: this.page.factory_id,
            businessco_no: this.page.businessco_no
        });
    }

    private async retrieveOperation(): Promise<void> {
        if (!this.page?.operation_id) return;

        const details = (await this.operation.getOperation({ operation_id: this.page.operation_id }))[0];

        if (details) {
            this.page.unit_no = details.unit_no ?? this.page.unit_no;
            this.page.adjustment_unit_name = details.adjustment_unit_name ?? this.page.adjustment_unit_name;
        }
    }

    private async modifyOperation(): Promise<void> {
        if (!this.page?.factory_id || !this.page.operation_id) return;

        const result = (await this.work.changeOperation({
            factory_id: this.page.factory_id,
            operation_id: this.page.operation_id
        }))[0];

        if (result) {
            this.page.workgrp_keyno = result.workgrp_keyno ?? this.page.workgrp_keyno;
            this.page.equipment_id = result.equipment_id ?? this.page.equipment_id;
        }
    }

    private async loadGrid(): Promise<void> {
        if (!this.page) return;

        const method = Number.parseInt(this.page.p2_datatask_keyno, 10);

        if (!Number.isFinite(method) || method <= 0) return;

        if (!this.grid) {
            const grid = new Grid<WorkSuboperationRow>()
                .setKendoConfig({ height: null, sortable: true, aggregate: true, pager: false, pageable: false, selectable: false, filterable: false })
                .setConfig({ onDataChanged: (event?: unknown) => this.onGridDataChanged(event) });

            if (this.page.view_id) grid.setRememberId(this.page.view_id);

            this.grid = grid;
        }

        const grid = this.grid;

        if (!grid?.dataTask) return;

        grid.dataTask.loadData = { method, parameters: this.page };
        await grid.gridfunc?.read();
    }

    private onGridDataChanged(event?: unknown): void {
        if (!event || typeof event !== 'object' || !this.grid?.gridfunc) return;

        const params = event as ValueSetterParams;
        const column = params.column?.getColId();

        if (column !== 'quantity' && column !== 'time_adjust_factor') return;

        const row = params.data as WorkSuboperationRow | undefined;

        if (!row) return;

        const quantity = Number(column === 'quantity' ? params.newValue : row.quantity);
        const timeAdjustFactor = Number(column === 'time_adjust_factor' ? params.newValue : row.time_adjust_factor);

        row.quantity_multiplied_by_std_time = quantity * Number(row.std_time);
        row.quantity_in_work_units = quantity * Number(row.std_time_in_work_units);
        row.adjusted_quantity_in_work_units = Number(row.quantity_in_work_units) * timeAdjustFactor;

        this.grid.gridfunc.updateRow({ ...row, _dirty: true });
        this.grid.gridfunc.refreshAggregates();
    }

    public ngOnInit(): void { void this.initialize(); }
}
